'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, History, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Version {
  id: string;
  name: string;
  timestamp?: string;
  isCurrent?: boolean;
}

interface VersionsDropdownProps {
  versions?: Version[];
  currentVersionId?: string;
  onVersionSelect?: (version: Version) => void;
  onViewHistory?: () => void;
}

// Default versions for demo
const defaultVersions: Version[] = [
  { id: '1', name: 'Version 1', isCurrent: true },
];

export function VersionsDropdown({
  versions = defaultVersions,
  currentVersionId,
  onVersionSelect,
  onViewHistory,
}: VersionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentVersion = versions.find(v => v.isCurrent || v.id === currentVersionId) || versions[0];

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700",
          isOpen && "bg-gray-50 border-brand-primary"
        )}
      >
        <History className="w-4 h-4" />
        <span className="text-sm font-medium">Versions</span>
        <ChevronDown className={cn(
          "w-4 h-4 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-[10px] shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-900">Version History</span>
          </div>

          {/* Version List */}
          <div className="max-h-64 overflow-y-auto">
            {versions.map((version) => {
              // If viewing a specific version (not 'current'), only highlight that version
              // Otherwise, highlight the version marked as isCurrent
              const isViewingSpecificVersion = currentVersionId && currentVersionId !== 'current';
              const isSelected = isViewingSpecificVersion 
                ? version.id === currentVersionId 
                : version.isCurrent;
              return (
                <button
                  key={version.id}
                  onClick={() => {
                    onVersionSelect?.(version);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left",
                    isSelected && "bg-brand-primary/5"
                  )}
                >
                  <div className="flex flex-col">
                    <span className={cn(
                      "text-sm",
                      isSelected ? "font-medium text-brand-primary" : "text-gray-700"
                    )}>
                      {version.name}
                      {version.isCurrent && (
                        <span className="ml-2 text-xs text-gray-500">(Current)</span>
                      )}
                    </span>
                    {version.timestamp && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {version.timestamp}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-4 h-4 text-brand-primary" />
                  )}
                </button>
              );
            })}
          </div>

          {/* View All History Link */}
          {onViewHistory && (
            <div className="border-t border-gray-100 px-4 py-2">
              <button
                onClick={() => {
                  onViewHistory();
                  setIsOpen(false);
                }}
                className="text-sm text-brand-primary hover:underline"
              >
                View all history
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

